// TODO: Replace with Intl.DurationFormat after good adoption
import { DurationFormat } from "@formatjs/intl-durationformat";

import Chainable from "./Chainable.mts";
import UArray from "./UArray.mts";
import UString from "./UString.mts";
import UZod from "./UZod.mts";

export const msIn = ((): Record<Duration.Unit, number> => {
  const second = 1000;
  const minute = second * 60;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;
  const year = day * 365.2425;
  const month = year / 12;

  return {
    second,
    minute,
    hour,
    day,
    week,
    month,
    year,
  } as const;
})();

class Duration {
  constructor(private ms: number) {}
  static parse(value: Duration.String) {
    return new Duration(Duration.String.toMs(value));
  }
}

namespace Duration {
  export type Unit = (typeof Unit.list)[number];
  export namespace Unit {
    export const list = [
      "second",
      "minute",
      "hour",
      "day",
      "week",
      "month",
      "year",
    ] as const;
  }
  export type String = `${number} ${Unit}`;
  export namespace String {
    export const is = (value: string): value is String =>
      !Number.isNaN(toMs(value as any));
    export function assert(value: string): asserts value is String {
      if (!is(value)) throw new Error("Invalid Duration.String");
    }
    export const zodSchema = UZod.predicate(is, "Invalid duration.");
    export const nonZero = (value: String | undefined): value is String =>
      !!+(value?.split(" ", 1)[0] || 0);
    export const toMs = <T extends String | undefined>(
      value: T,
    ): number | Extract<T, undefined> => {
      if (!value) return undefined!;
      const { count, unit } = parts(value);
      return count * msIn[unit];
    };
    export const parts = (value: String) => {
      const [count, unit] = UString.split(value, " ");
      return { count: +count, unit };
    };
    export const pluralize = (value: String) => {
      const { count, unit } = parts(value);
      return `${count} ${unit}${+count === 1 ? "" : "s"}`;
    };
    export const normalize = (value: string) => {
      const normalized = value.replace(/s$/, "");
      assert(normalized);
      return normalized;
    };
  }

  export const precisionOrder = Object.assign(Unit.list, {
    reversed: [...Unit.list].reverse() as unknown as readonly [
      "year",
      "month",
      "week",
      "day",
      "hour",
      "minute",
      "second",
    ],
  });

  export const format = Chainable.create(
    Chainable.wrapFactory(
      ({ maxUnits = Infinity }) =>
        (
          ms: number,
          style:
            | NonNullable<
                NonNullable<
                  ConstructorParameters<typeof DurationFormat>[1]
                >["style"]
              >
            | "per-unit",
        ) => {
          if (style === "per-unit") {
            if (ms === 0) return "-";
            const pair = UArray.minBy(
              precisionOrder.reversed.map(
                (precision) => [ms / msIn[precision], precision] as const,
              ),
              // Prioritize simpler amounts, e.g. prefer 90 minutes over 1.5 hours
              ([amount]) => [`${amount}`.length, amount],
            );
            if (!pair) return "-";
            if (pair[0] === 1) return `${pair[1]}`;
            return `${pair[0]} ${pair[1]}s`;
          }

          return ms < msIn.second
            ? "0"
            : new DurationFormat("en-US", { style }).format(
                Object.fromEntries(
                  precisionOrder.reversed
                    .map((unit) => {
                      // eslint-disable-next-line no-sparse-arrays
                      if (maxUnits === 0) return [, 0];
                      const count = Math.floor(ms / msIn[unit]);
                      if (count) {
                        ms -= count * msIn[unit];
                        --maxUnits;
                      }
                      return [`${unit}s`, count];
                    })
                    .filter(([, count]) => count),
                ),
              );
        },
    ),
    {
      maxUnits: (maxUnits: number) => ({ maxUnits }),
    },
  );
}

export default Duration;
